import { useRef, useState, useEffect, useMemo } from "react";
import DarkGradientPricing from "./dark-gradient-pricing/DarkGradientPricing";
import { PricingTable } from "./pricing-tier/PricingTable";
import CarouselArtists from "./carousel/CarouselArtists";
import grelo from "shared/assets/images/grelo-pricing.png";
import traiaVeia from "shared/assets/images/traia-veia.png";
import RayneRafaela from "shared/assets/images/rayane-rafaela.png";
import LucasMorato from "shared/assets/images/lucas-morato.png";
import { motion, useMotionValue, useAnimation } from "framer-motion";
import { useTranslation } from "react-i18next";
import SubscriptionFaq from "./subscription-faq/SubscriptionFaq";
import useSubscription, {
  SubscriptionPlan,
} from "shared/hooks/useSubscription";
import { useDispatch, useSelector } from "react-redux";
import {
  getSubscriptionPrices,
  getUserSubscription,
  SubscriptionState,
} from "explorer/reducers/subscription";
import {
  createCheckoutSession,
  createCustomerPortalSession,
  startTrial,
} from "shared/service/StrmService";
import { useHistory } from "react-router-dom";
import { openNewTab } from "shared/helpers/window-helpers";
import { toast } from "react-toastify";
import ModalPricing from "./modal-pricing/ModalPricing";
import "./Pricing.scss";
import { LabelServicesCalendlyLink } from "shared/helpers/calendly-contact";
import {
  dataLayerClickedEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { PricingButtonClickEventName } from "event-tracker/button-click-event-name";
import { moneyFormatObject } from "shared/helpers/BRLFormat";

const CARD_WIDTH = 350;
const GAP = 24; // 1.5rem = 24px
const SNAP_DISTANCE = CARD_WIDTH + GAP;

const containerVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring" as const,
      bounce: 0.3,
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const childVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring" as const,
      bounce: 0.3,
    },
  },
};

const Pricing = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { currentPlan, nextUpgrade, trial, paidPlan } = useSubscription();
  const { subscriptionInfo, subscriptionPrices, loadingSubscriptionPrices } =
    useSelector((state: SubscriptionState) => state.subscription);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [constraints, setConstraints] = useState<{
    left: number;
    right: number;
  }>({ left: 0, right: 0 });

  const [paymentPlan, setPaymentPlan] = useState<"monthly" | "yearly">(
    "monthly"
  );
  const [showMask, setShowMask] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<{
    isOpen: boolean;
    isDistribution: boolean;
    isLabel: boolean;
  }>({
    isOpen: false,
    isDistribution: false,
    isLabel: false,
  });

  const x = useMotionValue(0);
  const controls = useAnimation();

  const premiumRecommended = useMemo(() => {
    return nextUpgrade === SubscriptionPlan.PREMIUM;
  }, [nextUpgrade]);

  const distroRecommended = useMemo(() => {
    return nextUpgrade === SubscriptionPlan.DISTRIBUTION;
  }, [nextUpgrade]);

  const labelServicesRecommended = useMemo(() => {
    return nextUpgrade === SubscriptionPlan.LABEL_SERVICES;
  }, [nextUpgrade]);

  const handleDragEnd = (_event: any, info: any) => {
    const offset = info.offset.x;

    // Calcular para qual card fazer snap
    const currentX = x.get();
    let newX = currentX + offset;

    // Snap para o card mais próximo
    const snapIndex = Math.round(-newX / SNAP_DISTANCE);
    newX = -snapIndex * SNAP_DISTANCE;

    // Aplicar constraints - não deixa passar dos limites
    if (newX > 0) {
      newX = 0;
    }
    if (newX < constraints.left) {
      newX = constraints.left;
    }

    // Animar para a posição final com efeito elástico
    controls.start({
      x: newX,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 0.8,
      },
    });
  };

  const handlePremiumFreeTrial = async () => {
    try {
      dataLayerClickedEvent(PricingButtonClickEventName.PREMIUM_TRIAL_START, {
        ...tagManagerEvent.Pricing.PRICING_PAGE,
      });
      await startTrial();
      dispatch(getUserSubscription());
      history.push("/home?premium=success");
    } catch (error) {
      toast.error<string>(t("pages.Pricing.errors.unableToStartFreeTrial"));
    }
  };

  const handlePremiumGetStarted = async () => {
    dataLayerClickedEvent(PricingButtonClickEventName.PREMIUM_START, {
      ...tagManagerEvent.Pricing.PRICING_PAGE,
    });
    const response: any = await createCheckoutSession(paymentPlan);
    if (response.checkoutUrl) openNewTab(response.checkoutUrl);
    else {
      toast.error<string>(
        t("pages.Pricing.errors.unableToCreateCheckoutSession")
      );
    }
  };

  const handleDistributionGetStarted = async () => {
    dataLayerClickedEvent(PricingButtonClickEventName.START_DISTRIBUTION_PLAN, {
      ...tagManagerEvent.Pricing.PRICING_PAGE,
    });
    history.push("/distribua");
  };

  const showModalNotRecommended = (plan: SubscriptionPlan) => {
    if (plan === SubscriptionPlan.DISTRIBUTION) {
      dataLayerClickedEvent(PricingButtonClickEventName.DISTRIBUTION_PLAN, {
        ...tagManagerEvent.Pricing.PRICING_PAGE,
      });
    } else {
      dataLayerClickedEvent(PricingButtonClickEventName.LABEL_PLAN, {
        ...tagManagerEvent.Pricing.PRICING_PAGE,
      });
    }
    setShowModal({
      isOpen: true,
      isDistribution: plan === SubscriptionPlan.DISTRIBUTION,
      isLabel: plan === SubscriptionPlan.LABEL_SERVICES,
    });
  };

  const closeModal = () => {
    setShowModal({ isOpen: false, isDistribution: false, isLabel: false });
  };

  const onClickManageSubscription = async () => {
    if (paidPlan) {
      try {
        const response: any = await createCustomerPortalSession();
        if (response.portalUrl) {
          openNewTab(response.portalUrl);
        } else {
          toast.error<string>(
            t("pages.Pricing.errors.unableToCreatePortalSession")
          );
        }
      } catch (error) {
        toast.error<string>(
          t("pages.Pricing.errors.unableToCreatePortalSession")
        );
      }
    }
  };

  const onClickLabelServicesCalendly = () => {
    dataLayerClickedEvent(PricingButtonClickEventName.START_LABEL_PLAN, {
      ...tagManagerEvent.Pricing.PRICING_PAGE,
    });
    openNewTab(LabelServicesCalendlyLink);
  };

  const PricingPrice = () => {
    if (currentPlan === SubscriptionPlan.PREMIUM) {
      if (!trial) {
        return null;
      }
    }

    if (
      currentPlan === SubscriptionPlan.DISTRIBUTION ||
      currentPlan === SubscriptionPlan.LABEL_SERVICES
    ) {
      return null;
    }

    return (
      <div className="price-wrapper">
        <div className="price-toggle" role="tablist" aria-label="Pricing plans">
          <button
            type="button"
            className={`price-option ${
              paymentPlan === "monthly" ? "selected" : ""
            }`}
            onClick={() => setPaymentPlan("monthly")}
            aria-pressed={paymentPlan === "monthly"}
          >
            <div className="price-amount">
              {!loadingSubscriptionPrices
                ? moneyFormatObject({
                    amount: subscriptionPrices.monthlyAmount,
                    currency: subscriptionPrices.currency,
                  })
                : "..."}
            </div>
            <small className="price-note">
              {t("pages.Pricing.cards.perMonth")}
            </small>
          </button>

          <button
            type="button"
            className={`price-option ${
              paymentPlan === "yearly" ? "selected" : ""
            }`}
            onClick={() => setPaymentPlan("yearly")}
            aria-pressed={paymentPlan === "yearly"}
          >
            <div className="price-amount">
              {!loadingSubscriptionPrices
                ? moneyFormatObject({
                    amount: subscriptionPrices.yearlyAmount,
                    currency: subscriptionPrices.currency,
                  })
                : "..."}
            </div>
            <small className="price-note">
              {t("pages.Pricing.cards.perYear")}
            </small>
          </button>
        </div>
      </div>
    );
  };

  const ButtonsFree = () => {
    return (
      <button
        className="button-solo"
        onClick={() => {
          dataLayerClickedEvent(PricingButtonClickEventName.FREE_PLAN, {
            ...tagManagerEvent.Pricing.PRICING_PAGE,
          });
          history.push("/home");
        }}
      >
        {t("pages.Pricing.cards.exploreNow")}
      </button>
    );
  };

  const ButtonsPremium = () => {
    if (paidPlan) {
      return (
        <button className="button-void" onClick={onClickManageSubscription}>
          {t("pages.Pricing.cards.manageSubscription")}
        </button>
      );
    }
    if (trial) {
      return (
        <button className="button-solo" onClick={handlePremiumGetStarted}>
          {t("pages.Pricing.cards.upgradeNow")}
        </button>
      );
    }

    return (
      <>
        {subscriptionInfo.available && (
          <button
            className={
              nextUpgrade === SubscriptionPlan.PREMIUM
                ? "button-trial"
                : "button-solo"
            }
            onClick={handlePremiumFreeTrial}
          >
            {t("pages.Pricing.cards.startFreeTrial")}
          </button>
        )}
        <button
          className={
            nextUpgrade === SubscriptionPlan.PREMIUM
              ? "button-get-start"
              : "button-void"
          }
          style={{ marginTop: "1rem" }}
          onClick={handlePremiumGetStarted}
        >
          {t("pages.Pricing.cards.getStarted")}
        </button>
      </>
    );
  };

  const ButtonsDistribution = () => {
    if (currentPlan === SubscriptionPlan.DISTRIBUTION) {
      return (
        <button className="button-solo" onClick={() => history.push("/home")}>
          {t("pages.Pricing.cards.getStarted")}
        </button>
      );
    }
    if (
      nextUpgrade === SubscriptionPlan.DISTRIBUTION ||
      nextUpgrade === SubscriptionPlan.LABEL_SERVICES
    ) {
      return (
        <button
          className={
            nextUpgrade === SubscriptionPlan.DISTRIBUTION
              ? "button-trial"
              : "button-solo"
          }
          onClick={handleDistributionGetStarted}
        >
          {t("pages.Pricing.cards.distributeNow")}
        </button>
      );
    }
    return (
      <button
        className="button-solo"
        onClick={() => showModalNotRecommended(SubscriptionPlan.DISTRIBUTION)}
      >
        {t("pages.Pricing.cards.learnMore")}
      </button>
    );
  };

  const ButtonsLabelServices = () => {
    if (currentPlan === SubscriptionPlan.LABEL_SERVICES) {
      return (
        <button className="button-solo" onClick={() => history.push("/home")}>
          {t("pages.Pricing.cards.getStarted")}
        </button>
      );
    }
    if (nextUpgrade === SubscriptionPlan.LABEL_SERVICES) {
      return (
        <button className="button-trial" onClick={onClickLabelServicesCalendly}>
          {t("pages.Pricing.cards.applyNow")}
        </button>
      );
    }
    return (
      <button
        className="button-solo"
        onClick={() => showModalNotRecommended(SubscriptionPlan.LABEL_SERVICES)}
      >
        {t("pages.Pricing.cards.learnMore")}
      </button>
    );
  };

  useEffect(() => {
    const calculateConstraints = () => {
      if (carouselRef.current) {
        const children = carouselRef.current.children;
        const numCards = children.length;
        const viewportWidth = window.innerWidth;

        // Calcular a largura total do conteúdo (cards + gaps)
        const totalContentWidth = numCards * CARD_WIDTH + (numCards - 1) * GAP;

        // Definir o container width baseado no tamanho da tela
        let containerWidth;
        if (viewportWidth <= 1240) {
          // Mobile/Tablet: usar a largura disponível
          containerWidth = viewportWidth - 40; // 20px padding de cada lado
        } else {
          // Desktop: sempre 1200px
          containerWidth = 1200;
        }

        // O maxScroll é quanto precisamos arrastar para o último card colar na direita
        const maxScroll = totalContentWidth - containerWidth;

        setConstraints({ left: -Math.max(0, maxScroll), right: 0 });
      }
    };

    setTimeout(calculateConstraints, 100); // Espera renderizar
    calculateConstraints();
    window.addEventListener("resize", calculateConstraints);
    return () => window.removeEventListener("resize", calculateConstraints);
  }, []);

  useEffect(() => {
    const unsubscribe = x.on("change", (latest) => {
      // Verifica se está no final (último card visível)
      const threshold = 50; // margem de tolerância
      setShowMask(latest > constraints.left + threshold);
    });

    return () => unsubscribe();
  }, [x, constraints.left]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Pricing.PRICING_PAGE,
    });
  }, []);

  useEffect(() => {
    dispatch(getSubscriptionPrices());
  }, [dispatch]);

  return (
    <main id="pricing">
      <motion.div
        className="container container-margin-top"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <ModalPricing
          isOpen={showModal.isOpen}
          isDistribution={showModal.isDistribution}
          isLabel={showModal.isLabel}
          setIsOpen={closeModal}
        />
        <motion.div className="section-title" variants={childVariants}>
          <h1 className="title">{t("pages.Pricing.titles.mainTitle")}</h1>
          <div className="subtitle">
            {t("pages.Pricing.titles.mainSubtitle")}
          </div>
        </motion.div>

        <motion.div className="price-grid" variants={childVariants}>
          <DarkGradientPricing
            current={currentPlan === SubscriptionPlan.START}
            recommendation={
              <small>{t("pages.Pricing.cards.forAllLevels")}</small>
            }
            title={t("pages.Pricing.cards.start")}
            subtitle={t("pages.Pricing.cards.free")}
            description={t("pages.Pricing.cards.startDescription")}
            actions={<ButtonsFree />}
          />
          <DarkGradientPricing
            current={currentPlan === SubscriptionPlan.PREMIUM}
            trial={trial}
            recommended={premiumRecommended}
            title={t("pages.Pricing.cards.premium")}
            description={t("pages.Pricing.cards.premiumDescription")}
            recommendation={
              <small>
                <span style={{ color: "hsla(241, 100%, 90%, 1)" }}>
                  {t("pages.Pricing.cards.bestForLevels")}
                </span>{" "}
                1 {t("pages.Pricing.cards.to")} 3
              </small>
            }
            price={<PricingPrice />}
            actions={<ButtonsPremium />}
          />
          <DarkGradientPricing
            current={currentPlan === SubscriptionPlan.DISTRIBUTION}
            recommended={distroRecommended}
            recommendation={
              <small>
                <span style={{ color: "hsla(241, 100%, 90%, 1)" }}>
                  {t("pages.Pricing.cards.bestForLevels")}
                </span>{" "}
                4 {t("pages.Pricing.cards.to")} 6
              </small>
            }
            title={t("pages.Pricing.cards.distribution")}
            description={t("pages.Pricing.cards.distributionDescription")}
            subtitle={t("pages.Pricing.cards.royaltyShareModel")}
            actions={<ButtonsDistribution />}
          />
          <DarkGradientPricing
            current={currentPlan === SubscriptionPlan.LABEL_SERVICES}
            recommended={labelServicesRecommended}
            recommendation={
              <small>
                <span style={{ color: "hsla(241, 100%, 90%, 1)" }}>
                  {t("pages.Pricing.cards.bestForLevels")}
                </span>{" "}
                7 {t("pages.Pricing.cards.to")} 10
              </small>
            }
            title={t("pages.Pricing.cards.labelServices")}
            subtitle={t("pages.Pricing.cards.royaltyShareModel")}
            description={t("pages.Pricing.cards.labelServicesDescription")}
            actions={<ButtonsLabelServices />}
          />
        </motion.div>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
        className="container"
      >
        <motion.div variants={childVariants}>
          <PricingTable />
        </motion.div>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
        className="card-section-wrapper"
      >
        <motion.div variants={childVariants}>
          <small className="card-above-text">
            {t("pages.Pricing.titles.caseStudiesLabel")}
          </small>
          <h2 className="card-section-title">
            {t("pages.Pricing.titles.caseStudiesTitle")}
          </h2>
        </motion.div>
        <motion.div variants={childVariants}>
          <p className="card-right-text">
            {t("pages.Pricing.titles.caseStudiesDescription")}
          </p>
        </motion.div>
      </motion.div>
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
        className="artists-card-container"
      >
        <motion.div
          ref={carouselRef}
          className="artists-card-wrapper"
          drag="x"
          dragConstraints={constraints}
          dragElastic={0.2}
          onDragEnd={handleDragEnd}
          animate={controls}
          style={{ x }}
          variants={childVariants}
        >
          <CarouselArtists
            highlighted
            badge={t("pages.Pricing.carousel.badge")}
            title={t("pages.Pricing.carousel.traiaVeia.title")}
            subtitle={t("pages.Pricing.carousel.traiaVeia.subtitle")}
            description={t("pages.Pricing.carousel.traiaVeia.description")}
            imageUrl={traiaVeia}
          />
          <CarouselArtists
            title={t("pages.Pricing.carousel.grelo.title")}
            subtitle={t("pages.Pricing.carousel.grelo.subtitle")}
            description={t("pages.Pricing.carousel.grelo.description")}
            imageUrl={grelo}
          />
          <CarouselArtists
            title={t("pages.Pricing.carousel.rayaneRafaela.title")}
            subtitle={t("pages.Pricing.carousel.rayaneRafaela.subtitle")}
            description={t("pages.Pricing.carousel.rayaneRafaela.description")}
            imageUrl={RayneRafaela}
          />
          <CarouselArtists
            title={t("pages.Pricing.carousel.lucasMorato.title")}
            subtitle={t("pages.Pricing.carousel.lucasMorato.subtitle")}
            description={t("pages.Pricing.carousel.lucasMorato.description")}
            imageUrl={LucasMorato}
          />
        </motion.div>
        <div className={`artists-card-mask ${!showMask ? "hidden" : ""}`} />
      </motion.div>

      <div className="container">
        <SubscriptionFaq />
      </div>
    </main>
  );
};

export default Pricing;
